/**
 * <testimonial-carousel>
 *
 * Shows one testimonial at a time with a crossfade. Written as a custom element
 * so that every instance scopes its own queries — several carousels can live on
 * one page, and the theme editor re-upgrades them automatically whenever a
 * section is re-rendered.
 *
 * Attributes:
 *   data-autoplay        present to rotate automatically
 *   data-speed="7"       seconds between slides (default 7)
 */
class TestimonialCarousel extends HTMLElement {
  constructor() {
    super();
    this.index = 0;
    this.timer = null;
    this.onVisibilityChange = this.onVisibilityChange.bind(this);
  }

  connectedCallback() {
    this.slides = Array.from(this.querySelectorAll('[data-testimonial-slide]'));
    this.dots = Array.from(this.querySelectorAll('[data-testimonial-dot]'));
    if (this.slides.length === 0) return;

    this.prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const prev = this.querySelector('[data-testimonial-prev]');
    const next = this.querySelector('[data-testimonial-next]');
    if (prev) prev.addEventListener('click', () => this.step(-1, true));
    if (next) next.addEventListener('click', () => this.step(1, true));

    this.dots.forEach((dot, i) => {
      dot.addEventListener('click', () => this.goTo(i, true));
    });

    this.addEventListener('keydown', this.onKeydown.bind(this));

    // Pause while the visitor is reading or interacting.
    this.addEventListener('mouseenter', () => this.stop());
    this.addEventListener('mouseleave', () => this.start());
    this.addEventListener('focusin', () => this.stop());
    this.addEventListener('focusout', () => {
      if (!this.contains(document.activeElement)) this.start();
    });

    this.addTouchListeners();
    document.addEventListener('visibilitychange', this.onVisibilityChange);

    this.goTo(0);
    this.start();
  }

  disconnectedCallback() {
    this.stop();
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
  }

  onVisibilityChange() {
    if (document.hidden) {
      this.stop();
    } else {
      this.start();
    }
  }

  onKeydown(event) {
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.step(-1, true);
    } else if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.step(1, true);
    }
  }

  addTouchListeners() {
    let startX = null;

    this.addEventListener(
      'touchstart',
      (event) => {
        startX = event.changedTouches[0].clientX;
        this.stop();
      },
      { passive: true }
    );

    this.addEventListener(
      'touchend',
      (event) => {
        if (startX === null) return;
        const delta = event.changedTouches[0].clientX - startX;
        if (Math.abs(delta) > 40) this.step(delta < 0 ? 1 : -1, true);
        startX = null;
        this.start();
      },
      { passive: true }
    );
  }

  step(direction, userInitiated) {
    this.goTo((this.index + direction + this.slides.length) % this.slides.length, userInitiated);
  }

  goTo(index, userInitiated) {
    this.index = index;

    this.slides.forEach((slide, i) => {
      const active = i === index;
      slide.classList.toggle('is-active', active);
      // visibility:hidden already removes inactive slides from the a11y tree;
      // aria-hidden keeps the intent explicit for older assistive tech.
      if (active) {
        slide.removeAttribute('aria-hidden');
      } else {
        slide.setAttribute('aria-hidden', 'true');
      }
    });

    this.dots.forEach((dot, i) => {
      const active = i === index;
      dot.classList.toggle('is-active', active);
      dot.setAttribute('aria-selected', active ? 'true' : 'false');
    });

    // Restart the countdown so a manual change gets a full reading interval.
    if (userInitiated) this.start();
  }

  start() {
    this.stop();
    if (!this.hasAttribute('data-autoplay') || this.slides.length < 2) return;
    if (this.prefersReducedMotion) return;

    const seconds = parseInt(this.dataset.speed, 10) || 7;
    this.timer = setInterval(() => this.step(1), seconds * 1000);
  }

  stop() {
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }
  }
}

if (!customElements.get('testimonial-carousel')) {
  customElements.define('testimonial-carousel', TestimonialCarousel);
}

/*
 * Theme editor: when a merchant selects a testimonial block in the sidebar, jump
 * the carousel to it and hold it there so the block they are editing stays on
 * screen.
 */
document.addEventListener('shopify:block:select', (event) => {
  const carousel = event.target.closest('testimonial-carousel');
  if (!carousel) return;

  const slides = Array.from(carousel.querySelectorAll('[data-testimonial-slide]'));
  const index = slides.indexOf(event.target);
  if (index === -1) return;

  carousel.goTo(index);
  carousel.stop();
});

document.addEventListener('shopify:block:deselect', (event) => {
  const carousel = event.target.closest('testimonial-carousel');
  if (carousel) carousel.start();
});
